import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { useError, useForm } from '../../hooks';
import { useFirebaseCRUD } from '../../hooks/useFirebaseCRUD';
import { waitForEnvClaim } from '../../firebase';

import TextField from '../Form/TextField';
import DropdownField from '../Form/DropdownField';
import Button from '../Button';
import ContextMenu from '../ContextMenu';
import Spinner from '../Spinner';

export default function ({ organizationId }) {
  const navigate = useNavigate();
  const { getDocument, getCollection, createDocument } = useFirebaseCRUD();
  const setError = useError();

  const [organization, setOrganization] = useState(null);
  const [organizations, setOrganizations] = useState([]);
  const [loading, setLoading] = useState(true);

  const [getFieldProps, formData, formError] = useForm();

  // ✅ Load organization and list of other organizations
  useEffect(() => {
    async function fetchData() {
      setLoading(true);
      try {
        const org = await getDocument(`organizations/${organizationId}`);
        // An UNFILTERED list, on purpose. Picking a dispatch target means
        // picking an organisation you are not bound to yet, so the rules cannot
        // scope this read the way they scope every other organisation read —
        // `allow list: if member()` in firestore.rules exists for this screen.
        // Note getCollection swallows a permission-denied and returns [], which
        // is how the old denial showed up as a silently empty dropdown.
        let orgs = await getCollection('organizations');

        // ✅ Filter out the current organization just commment to add current organization to the list
        // orgs = orgs.filter(({ id }) => id !== organizationId);

        setOrganization(org);
        setOrganizations(orgs);
      } catch (err) {
        setError(err);
      } finally {
        setLoading(false);
      }
    }

    fetchData();
  }, [organizationId]);

  if (loading || !organization) {
    return <Spinner />;
  }

  async function handleSubmit(e) {
    e.preventDefault();

    let { dispatchOrganizationId } = formData;


    try {
      const selectedOrg = organizations.find(
        (org) => org.id == dispatchOrganizationId
      );

      const bindingId = `${dispatchOrganizationId}`;
      // createDocument returns false on a denied write instead of throwing, so
      // without this check the screen navigated away as though it had worked.
      const created = await createDocument(
        `organizations/${organizationId}/dispatch-bindings/${bindingId}`,
        {
          dispatchOrganization: {
            defaultUserType: {
              id: selectedOrg.defaultUserTypeId || "", 
            },
            id: selectedOrg.id,
            name: selectedOrg.name,
          },
          dispatchOrganizationId,
          createdAt: new Date().toISOString(),
        }
      );

      if (!created) {
        throw new Error(
          'Could not save the dispatch binding. Only an admin of this ' +
          'organisation may add one.'
        );
      }

      // The rules read boundOrgIds from the ID TOKEN, not from the binding
      // document. Without this wait the binding is saved and inert: the new
      // organisation's users and sites stay denied, and the dispatch picker
      // stays short, until Firebase happens to refresh the token.
      await waitForEnvClaim(
        (claim) => (claim.boundOrgIds || []).includes(`${dispatchOrganizationId}`)
      );

      navigate(`/organizations/${organization.id}`);
    } catch (err) {
      if (err.message === 'validation_error') {
        formError(err.props);
      } else {
        setError(err);
      }
    }
  }

  return (
    <div className="box">
      <form onSubmit={handleSubmit}>
        <TextField label="Organization" value={organization.name} readOnly={true} />

        <DropdownField
          name="dispatchOrganizationId"
          label="Dispatch Organization"
          {...getFieldProps()}
          required
        >
          <option />
          {organizations.map(({ id, name }) => (
            <option value={id} key={id}>
              {name}
            </option>
          ))}
        </DropdownField>

        <div className='flex justify-end'>
          <Button type="submit">Create</Button>
        </div>
      </form>
    </div>
  )
}
